import { Video, MapPin, Users2, Star, Globe2, X, Tag } from "lucide-react";
import { MARKETPLACE } from "@/constants/testIds";

// ---------------------------------------------------------------------------
// MarketplaceFilters — sidebar (desktop) / drawer (mobile).
// State-only component: parent owns the filters object and passes a
// `patch(nextPartial)` callback plus the facet counts from the backend.
// ---------------------------------------------------------------------------
export default function MarketplaceFilters({
  filters,
  facets,
  patch,
  onClose,   // only used in the mobile drawer
  variant = "sidebar",
}) {
  const isDrawer = variant === "drawer";
  const sectionCls = "py-5 border-b border-slate-100 last:border-b-0";

  return (
    <aside
      data-testid={MARKETPLACE.filtersPanel}
      className={
        isDrawer
          ? "fixed inset-0 z-50 bg-white overflow-y-auto"
          : "sticky top-24 self-start w-[260px] shrink-0 hidden md:block"
      }
    >
      {isDrawer && (
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="eyebrow">§ Filters</div>
          <button
            onClick={onClose}
            data-testid={MARKETPLACE.filtersMobileClose}
            className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500"
            aria-label="Close filters"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className={isDrawer ? "px-5 pb-24" : ""}>
        {!isDrawer && <div className="eyebrow pb-3">§ Filters</div>}

        {/* Specialty */}
        <div className={sectionCls}>
          <Label>
            <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" /> Specialty</span>
          </Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(facets?.specialties || []).slice(0, 10).map((sp) => (
              <button
                key={sp.value}
                type="button"
                onClick={() => patch({ specialty: filters.specialty === sp.value ? "" : sp.value })}
                data-testid={MARKETPLACE.specialtyFilter(sp.value)}
                className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11.5px] font-medium border transition-colors ${
                  filters.specialty === sp.value
                    ? "bg-[#2563EB] text-white border-[#2563EB]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}
              >
                {sp.value}
                <span className="text-[10px] opacity-60">{sp.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Session type */}
        <div className={sectionCls}>
          <Label>Session type</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <ChoicePill
              testId={MARKETPLACE.modeFilter("online")}
              active={filters.mode === "online"}
              onClick={() => patch({ mode: filters.mode === "online" ? "" : "online" })}
              icon={Video}
              label="Online"
              badge={facets?.modes?.online}
            />
            <ChoicePill
              testId={MARKETPLACE.modeFilter("in_person")}
              active={filters.mode === "in_person"}
              onClick={() => patch({ mode: filters.mode === "in_person" ? "" : "in_person" })}
              icon={MapPin}
              label="In person"
              badge={facets?.modes?.in_person}
            />
          </div>
        </div>

        {/* City */}
        <div className={sectionCls}>
          <Label>Location</Label>
          <input
            type="text"
            value={filters.city || ""}
            onChange={(e) => patch({ city: e.target.value })}
            placeholder="Search city, e.g. Brooklyn"
            className="mt-2 w-full h-10 rounded-lg bg-slate-50 border border-slate-200 px-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(facets?.cities || []).slice(0, 6).map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => patch({ city: filters.city === c.value ? "" : c.value })}
                data-testid={MARKETPLACE.cityFilter(c.value)}
                className={`h-7 px-2.5 rounded-full text-[11.5px] font-medium border transition-colors ${
                  filters.city === c.value
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}
              >
                {c.value} <span className="text-[10px] opacity-60">{c.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Price */}
        <div className={sectionCls}>
          <Label>Price / session</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <MoneyInput
              testId={MARKETPLACE.priceMin}
              value={filters.price_min}
              onChange={(v) => patch({ price_min: v })}
              placeholder="Min"
            />
            <MoneyInput
              testId={MARKETPLACE.priceMax}
              value={filters.price_max}
              onChange={(v) => patch({ price_max: v })}
              placeholder="Max"
            />
          </div>
          <p className="mt-2 text-[10.5px] text-slate-400">
            Catalogue range: ${facets?.price?.min ?? "—"}–${facets?.price?.max ?? "—"}
          </p>
        </div>

        {/* Rating */}
        <div className={sectionCls}>
          <Label>Rating</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[
              { v: 4.5, label: "4.5+" },
              { v: 4.0, label: "4.0+" },
              { v: 3.5, label: "3.5+" },
              { v: 0,   label: "Any" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => patch({ rating_min: Number(filters.rating_min || 0) === opt.v ? "" : (opt.v || "") })}
                data-testid={MARKETPLACE.ratingFilter(opt.v)}
                className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-[11.5px] font-medium border transition-colors ${
                  Number(filters.rating_min || 0) === opt.v
                    ? "bg-[#2563EB] text-white border-[#2563EB]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}
              >
                <Star className="w-3 h-3" strokeWidth={2.4} /> {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Experience level fit */}
        <div className={sectionCls}>
          <Label>Client level fit</Label>
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {["beginner", "intermediate", "advanced"].map((lvl) => (
              <button
                key={lvl}
                type="button"
                onClick={() => patch({ level: filters.level === lvl ? "" : lvl })}
                data-testid={MARKETPLACE.levelFilter(lvl)}
                className={`h-9 rounded-lg text-[11.5px] font-medium border capitalize transition-colors ${
                  filters.level === lvl
                    ? "bg-[#10B981] text-white border-[#10B981]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}
              >
                {lvl}
              </button>
            ))}
          </div>
        </div>

        {/* Language */}
        <div className={sectionCls}>
          <Label>Language</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(facets?.languages || []).slice(0, 8).map((l) => (
              <button
                key={l.value}
                type="button"
                onClick={() => patch({ language: filters.language === l.value ? "" : l.value })}
                data-testid={MARKETPLACE.languageFilter(l.value)}
                className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11.5px] font-medium border transition-colors ${
                  filters.language === l.value
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}
              >
                <Globe2 className="w-3 h-3" strokeWidth={2.4} /> {l.value}
                <span className="text-[10px] opacity-60">{l.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Availability */}
        <div className={sectionCls}>
          <Label>Availability</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[
              { key: "weekdays", label: "Weekdays" },
              { key: "weekends", label: "Weekends" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => patch({ availability: filters.availability === opt.key ? "" : opt.key })}
                data-testid={MARKETPLACE.availabilityFilter(opt.key)}
                className={`h-9 rounded-lg text-[11.5px] font-medium border transition-colors ${
                  filters.availability === opt.key
                    ? "bg-[#2563EB] text-white border-[#2563EB]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isDrawer && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-4">
          <button
            onClick={onClose}
            className="w-full h-11 rounded-full bg-slate-900 text-white text-[13.5px] font-semibold hover:bg-[#2563EB] transition-colors"
          >
            Show results
          </button>
        </div>
      )}
    </aside>
  );
}

// -----------------------------------------------------------------------
function Label({ children }) {
  return (
    <label className="text-[11px] tracking-[0.22em] uppercase text-slate-500 font-semibold">
      {children}
    </label>
  );
}

function ChoicePill({ testId, active, onClick, icon: Icon, label, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`h-9 rounded-lg text-[12px] font-medium border inline-flex items-center justify-center gap-1.5 transition-colors ${
        active ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
      }`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
      {label}
      {typeof badge === "number" && (
        <span className={`text-[10px] ${active ? "opacity-80" : "text-slate-400"}`}>{badge}</span>
      )}
    </button>
  );
}

function MoneyInput({ testId, value, onChange, placeholder }) {
  return (
    <div className="relative">
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">$</span>
      <input
        type="number"
        min={0}
        step={5}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value ? parseInt(e.target.value, 10) : "")}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full h-10 rounded-lg bg-slate-50 border border-slate-200 pl-6 pr-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
      />
    </div>
  );
}
